import { apiClient } from '../api/apiClient';
import { appConfig } from '../config/appConfig';
import memoriesData from '../data/memories.json';
import type { Memory, NewMemory } from '../models/Memory';
import { delay } from '../utils/delay';

export interface MemoryService {
  getMemories(): Promise<Memory[]>;
  addMemory(memory: NewMemory): Promise<Memory>;
}

/** Newest first, which is how every view of memories is presented. */
export function sortByNewestFirst(memories: readonly Memory[]): Memory[] {
  return [...memories].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  );
}

/**
 * In-memory implementation used while the Worker does not exist.
 *
 * Submissions are kept for the lifetime of the page only -- reloading restores
 * the demonstration data. The store is closed over rather than exported so no
 * component can reach past the service to mutate it.
 */
export function createMockMemoryService(seed: readonly Memory[] = memoriesData): MemoryService {
  let memories: Memory[] = sortByNewestFirst(seed);

  return {
    async getMemories() {
      await delay(200);
      return [...memories];
    },

    async addMemory(memory: NewMemory) {
      await delay(400);
      const created: Memory = {
        // The real identifier will be generated by the Worker, never the browser.
        id: `local-${crypto.randomUUID()}`,
        name: memory.name,
        message: memory.message,
        created: new Date().toISOString(),
        ...(memory.photoId ? { photoId: memory.photoId } : {}),
      };
      memories = sortByNewestFirst([created, ...memories]);
      return created;
    },
  };
}

export function createHttpMemoryService(): MemoryService {
  return {
    async getMemories() {
      const memories = await apiClient.get<Memory[]>('/api/memories');
      return sortByNewestFirst(memories);
    },

    addMemory(memory: NewMemory) {
      return apiClient.post<Memory>('/api/memories', memory);
    },
  };
}

export const memoryService: MemoryService = appConfig.useMockData
  ? createMockMemoryService()
  : createHttpMemoryService();
